import { Component, OnDestroy } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { take } from "rxjs/operators";
import * as lodash from "lodash";
import * as CryptoJS from "crypto-js";
import {
  ITCIMarketplaceListing,
  TCI_MARKETPLACE_LISTING_VIEW_MODE,
  TciMarketplaceV2Filters,
  TCIMarketplaceV2FilterCategory,
  TCIMarketplaceListingStatus,
  ITCIAssetTag,
  ITCIUserInfo,
  TCI_ENVIRONMENT_VAR_TYPE,
  ITCIEnvVars,
  TCI_MARKETPLACE_NOTIFICATION_TYPE,
  USER_ROLES,
  ITCIMarketplaceV2FiltersCategoryIcon, TCIMainMenu
} from "@tci/common";
import { SelectItem } from "primeng/primeng";
import { FormControl } from "@angular/forms";
import { NgToolsService } from "../ng-shared/ng-util/ng-tools.service";
import { NgEnvVarsService } from "../ng-shared/ng-services/ng-env-vars.service";
import { NgTagsService } from "../ng-shared/ng-services/ng-tags.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { NgMarketplaceV2Service } from "./ng-marketplace-v2.service";
import { routeChildReduce } from "../ng-shared/ng-util/tools";

interface IFilterCategoryItem {
  subType?: TciMarketplaceV2Filters;
  isMultiSelection: boolean;
  category: ITCIMarketplaceV2FiltersCategoryIcon;
  count: number;
}

interface IFilterCategory {
  type: TCIMarketplaceV2FilterCategory;
  id: string;
  label: string;
  items: IFilterCategoryItem[];
}
const MK_SORTABLE_FIELD = {
  DISPLAY_NAME: { label: "Display name", value: "displayName" },
  LAST_CERTIFIED_DATE: { label: "Last certified date", value: "lastCertifiedDate" }
};
const MK_SORTABLE_ASC = {
  ASC: { label: "Asc", value: true },
  DESC: { label: "Desc", value: false },
};
const MK_LOCAL_ITEM = "TCI-MK-LOCAL-DATA";
const MK_LOCAL_ITEM_PREFIX = "tci_mk_";
const FILTER_LABEL_ALL = "All";

interface IStoredMarketPlaceFilter {
  filter: {
    assetType: string, capability: string, more: string, myManagingContributions: string, tags: ITCIAssetTag[], searchName?: string
  };
  sort: { column: string, asc: boolean };
  lastRequestTime?: number;
  selectedListingId?: string;
  page?: number;
}

const MK_ROUTE = TCIMainMenu.MAIN_MENU_MARKETPLACE_V2.getRouteId(); // "marketplace"

@Component({
  selector: "ng-marketplace-v2",
  templateUrl: "./ng-marketplace-v2.component.html",
  styleUrls: ["./ng-marketplace-v2.component.less"]
})
export class NgMarketplaceV2Component implements OnDestroy {

  private subscriptions: Subscription[] = [];
  private userInfo: ITCIUserInfo;
  private storeSelectedFilterWorker: Subscription;

  // For the cards listing
  listingsData: ITCIMarketplaceListing[] = [];
  filledListingData = new Array(10);    // Fixed IPAS-17202
  filteredListingsData: ITCIMarketplaceListing[] = [];
  listingMode = TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD;
  isLoading = true;
  isSortAsc = true;

  // For details
  selectedListingIdFromUrl: string;
  selectedListing: ITCIMarketplaceListing;
  detailMode = TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL;

  FILTER_CATEGORIES: IFilterCategory[] = [];
  FILTER_SELECTED: string[] = [];
  FILTER_OWNER_NAME: string;
  FILTER_SORT_BY: string = MK_SORTABLE_FIELD.DISPLAY_NAME.value;
  FILTER_SORT_ASC = true;
  FILTER_LAST_REQUEST_TIME = 0;
  FILTER_CURRENT_REQUEST_TIME = 0;

  isDisableRequestNewButton = false;
  requestConnectorUrl = "";

  tagsList: string[] = [];
  allAssetTags: ITCIAssetTag[] = [];
  allAssetTagsCount = 0;
  selectedTagsCount = 0;
  tagsFilterVisible = false;
  FILTER_SELECTED_TAGS: ITCIAssetTag[] = [];

  SORT_BY_OPTIONS: SelectItem[] = [
    MK_SORTABLE_FIELD.DISPLAY_NAME,
    MK_SORTABLE_FIELD.LAST_CERTIFIED_DATE
  ];
  SORT_ASC_OPTIONS: SelectItem[] = [
    MK_SORTABLE_ASC.ASC,
    MK_SORTABLE_ASC.DESC
  ];
  nameFilterCtrl = new FormControl();

  constructor(private mkService: NgMarketplaceV2Service,
    private router: Router,
    private route: ActivatedRoute,
    private toolsSvc: NgToolsService,
    private envVarService: NgEnvVarsService,
    private tagsService: NgTagsService,
    private userService: NgUserService) {

    // Check route for listingId. This is "landing with params" case.
    const routeCheck = Observable.merge(...this.route.children.map(c => c.params))
      .subscribe(params => {
        if (routeCheck) { // This event should react only once
          routeCheck.unsubscribe();
          return;
        }

        this.selectedListingIdFromUrl = params["listingId"];
        this.initData();
        this.initSelectedListing();
      });
    this.subscriptions.push(
      routeCheck,
      this.getRequestConnectorUrl(),

      // Listen to route navigations for detecting listing selection
      this.router.events
        .filter(evnt => evnt.url.startsWith("/" + MK_ROUTE))
        .subscribe((evnt: any) => {
          if (evnt.state && (evnt.state.root || evnt.state._root)) {
            const root = evnt.state.root || evnt.state._root;
            const params: any = lodash.assign({}, routeChildReduce(root));

            this.selectedListingIdFromUrl = params["listingId"];
            this.checkListingFromRoute(true);
          }
        }),

      // Listen to updates from listing editor
      // IMPORTANT: This should connect to the access editor too!!!
      mkService.getNotificationSubject()
        .filter(() => this.listingsData && this.listingsData.length > 0)
        .subscribe(change => {
          console.log("Marketplace listing change detected", change);
          if (change.type === TCI_MARKETPLACE_NOTIFICATION_TYPE.Delete) {
              this.deleteListing(change.listing.id);
          } else if (change.type === TCI_MARKETPLACE_NOTIFICATION_TYPE.Create) {
              this.listingsData.push(change.listing as ITCIMarketplaceListing);
          } else {
              const index = this.listingsData.findIndex(l => l.id === change.listing.id);
              const listing = this.listingsData[index];
              Object.assign(listing, change.listing);

              // const filteredIndex = this.filteredListingsData.findIndex(l => l.id === change.listing.id);
              // if (filteredIndex >= 0) { // Item is present in filtered list
              //   const filteredListing = this.filteredListingsData[index];
              //   Object.assign(filteredListing, change.listing);
              // }
              // Refresh tag count when change notification recieved in edit mode.
              if (this.tagsList) {
                  this.countListingPerTag();
              }
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions
      .filter(s => !s.closed)
      .forEach(s => s.unsubscribe());
  }

  selectListing(asset: ITCIMarketplaceListing) {
    const path: any[] = [MK_ROUTE];
    if (asset) {
      path.push(asset.id);
    }

    this.router.navigate(path, { preserveQueryParams: true, preserveFragment: true });
  }

  filterListingBySubCategory(category: TCIMarketplaceV2FilterCategory, subCategory: IFilterCategoryItem) {
    if (subCategory.isMultiSelection) {
      // Note: for multi selection, the FILTER_SELECTED is an array,
      // and it does not contains "All", and all the item is unique
      this.FILTER_SELECTED[category.getId()] = lodash.uniq(lodash.compact(lodash.reject(
        lodash.concat([], this.FILTER_SELECTED[category.getId()], subCategory.category.id),
        item => item === FILTER_LABEL_ALL
      )));
    } else {
      this.FILTER_SELECTED[category.getId()] = subCategory.category.id;
    }

    this.filterMarketplaceListingData();
  }

  isHighLightFilter(categoryId: string, selectedCategoryId: string, isMultiSelection: boolean) {
    let bool: boolean;
    if (isMultiSelection) {
      bool = lodash.compact(lodash.concat([], this.FILTER_SELECTED[selectedCategoryId])).includes(categoryId);
    } else {
      bool = categoryId === this.FILTER_SELECTED[selectedCategoryId];
    }
    return bool;
  }

  /**
   * "My Managing Contributions" filter for provider org admin only
   * @param category
   */
  isShowFilterCategory(categoryId: string) {
    const isShowFilter = true;
    /*
    some filter are only for specified user org, need to check then determine to show or hide it.
    but this feature has been disabled for now.
    // TODO: how to know current user a provider org admin?
    const isProviderOrgAdmin = true;
    // Note: only provider org admin can see "My Managing Contributions" filter
    if (categoryId === TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER.getId()) {
      isShowFilter = isProviderOrgAdmin;
    }
    */
    return isShowFilter;
  }

  filterSortBy(sortBy: string) {
    if (sortBy === MK_SORTABLE_FIELD.DISPLAY_NAME.value) {
      this.filteredListingsData = this.filteredListingsData
        .sort((a, b) => this.alphaNumSort(a.displayName.toString().trim(), b.displayName.toString().trim()));
    } else if (sortBy === MK_SORTABLE_FIELD.LAST_CERTIFIED_DATE.value) {
      // IPAS-17220: items without lastCertifiedDate will show in last
      this.filteredListingsData = lodash.orderBy(
        this.filteredListingsData, [data =>  data.lastCertifiedDate || ""], ["desc"]
      );
    }

    if (!this.FILTER_SORT_ASC) {
      this.filteredListingsData.reverse();
    }
    this.storeSelectedFilter();
  }

  filterSortAsc() {
    // Do not sort if same option is selected again.
    if (this.isSortAsc === this.FILTER_SORT_ASC) {
      return;
    }
    this.filteredListingsData.reverse();
    this.storeSelectedFilter();
    this.isSortAsc = this.FILTER_SORT_ASC;
  }

  clearAllFilter() {
    this.FILTER_SELECTED = [];
    this.FILTER_SELECTED_TAGS = [];
    this.FILTER_SORT_BY = MK_SORTABLE_FIELD.DISPLAY_NAME.value;
    this.FILTER_SORT_ASC = true;

    delete this.selectedListingIdFromUrl;
    this.checkListingFromRoute();

    this.nameFilterCtrl.setValue("", { emitEvent: false });
    this.filterMarketplaceListingData();
  }

  changeTagsSelection(tags: ITCIAssetTag[]): void {
    if (!lodash.isEqual(tags, this.FILTER_SELECTED_TAGS)) {
      this.FILTER_SELECTED_TAGS = lodash.cloneDeep(tags);
      this.filterMarketplaceListingData();
    }
  }

  deleteListing(id: string) {
    this.initMarketplaceListingAndTags();
    this.mkService.selectListing(null);
    this.router.navigate([MK_ROUTE], { preserveQueryParams: true, preserveFragment: true });
  }

  /**
   * Check if the selected listing id from route is valid.
   * If it's not then navigate to root ONLY if the check
   * does not come from the router watcher itself.
   */
  private checkListingFromRoute(fromRoute = false) {
    const clear = () => {
      if (!fromRoute) {
        this.mkService.selectListing(null);
        this.router.navigate([MK_ROUTE], { preserveQueryParams: true, preserveFragment: true });
      }
    };

    if (this.selectedListingIdFromUrl) {
      const asset = this.listingsData.find(item => item.id === this.selectedListingIdFromUrl);
      if (asset) {
        this.mkService.selectListing(asset);
        return true;
      } else {
        console.warn("Marketplace listing is not present", this.selectedListingIdFromUrl);
        delete this.selectedListingIdFromUrl;
        clear();
      }
    } else {
      clear();
    }
  }

  private getRequestConnectorUrl() {
    return this
      .envVarService
      .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_SCRIBE_REQUEST_CONNECTOR_URL)
      .subscribe(
        (envVars: ITCIEnvVars<string>) => {
          this.requestConnectorUrl = envVars[TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_SCRIBE_REQUEST_CONNECTOR_URL];
        }
      );
  }

  private initData() {
    this.subscriptions.push(
      this.userService.getUserInfo()
        .finally(() => this.initMarketplaceListingAndTags())
        .subscribe((userInfo) => {
          this.userInfo = userInfo;
          this.isDisableRequestNewButton = userInfo.rolX[0] === USER_ROLES.RO;
          this.FILTER_OWNER_NAME = userInfo.userId;
        })
    );

    this.subscriptions.push(
      this.nameFilterCtrl.valueChanges
        .debounceTime(300)
        .subscribe(() => {
          this.filterMarketplaceListingData();
        })
    );
  }

  private initMarketplaceListingAndTags() {
    // Request tags and apps, also catch api error response
    // Note: in app list page, when navigate to marketplace, then immediately switch back to app list page,
    // the page will be redirected to marketplace page again, need to unsubscribe below Observable.
    this.subscriptions.push(Observable.combineLatest(
      this.mkService.getMarketplaceTags().catch(e => {
        console.error("Marketplace error getting listings' tags", e);
        return Observable.of([]);
      }),
      this.mkService.getAllListings().catch(e => {
        console.error("Marketplace error getting listings", e);
        return Observable.of(null);
      }),
      (tags, listings) => ({ tags, listings })
    )
      .pipe(take(1))
      .flatMap(listingsData =>
        this.requestTags(listingsData.tags).map(tags => ({ tags, listingsData }))
      )
      .subscribe(data => {
        if (data.listingsData.listings !== null) {
          this.listingsData = data.listingsData.listings;
          this.isLoading = false;
          this.applyStoredFilterToMarketplaceListingData(this.listingsData);

          // Note: only update request time when getAllListing() really return values.
          this.FILTER_CURRENT_REQUEST_TIME = new Date().getTime();
        }
      }));
  }

  // "requestTags" is the last step on building the marketplace list.
  // It is in charge of setting the flags for the template to render.
  private requestTags(tagsArray?: string[]): Observable<ITCIAssetTag[]> {
    const tagsObs: Observable<string[]> = !tagsArray ?
      this.mkService.getMarketplaceTags() : Observable.of(tagsArray);

    return tagsObs.map(resTags => {
      this.tagsList = resTags;

      const tags: ITCIAssetTag[] = [];
      resTags.forEach(tag => {
        tags.push({
          "name": tag
        });
      });

      this.allAssetTags = tags;
      this.tagsFilterVisible = true;
      return tags;
    });
  }

  private alphaNumSort(comparison1: string, comparison2: string) {
    return comparison1.localeCompare(comparison2, "en", { numeric: true, caseFirst: "upper" });
  }

  private getFilterCategories(listingData: ITCIMarketplaceListing[]): IFilterCategory[] {
    const filters: IFilterCategory[] = [];
    TCIMarketplaceV2FilterCategory.getAllCategories()
      .forEach((category) => {
        const items: IFilterCategoryItem[] = [];
        items[FILTER_LABEL_ALL] = {
          "isMultiSelection": false,
          "category": {
            "id": FILTER_LABEL_ALL,
            "label": FILTER_LABEL_ALL,
            "iconUrl": "",
          },
          "count": listingData.length
        };
        TciMarketplaceV2Filters.getFilters(category).forEach((subCategory: TciMarketplaceV2Filters) => {
          items[subCategory.id] = {
            "subType": subCategory,
            "isMultiSelection": !!subCategory.getCategory().getSelectType(),
            "category": {
              "id": subCategory.id,
              "label": subCategory.label,
              "iconUrl": subCategory.iconUrl,
            },
            "count": this.filterListingCountBySubCategory(subCategory)
          };
        });

        filters[category.getId()] = {
          "type": category,
          "id": category.getId(),
          "label": category.getLabel(),
          "items": items
        };
      });
    return filters;
  }

  private applyStoredFilterToMarketplaceListingData(listingData: ITCIMarketplaceListing[]) {
    const storedFilterData = localStorage.getItem(this.getMarketplaceFilterPrefForUser());
    if (!lodash.isEmpty(storedFilterData)) {
      try {
        const filtersAndPagination: IStoredMarketPlaceFilter = JSON.parse(storedFilterData);

        this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE.getId()] = filtersAndPagination.filter.assetType;

        this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY.getId()] = filtersAndPagination.filter.capability;

        this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER.getId()] = filtersAndPagination.filter.more;

        // tslint:disable-next-line:max-line-length
        this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER.getId()] = filtersAndPagination.filter.myManagingContributions;

        this.FILTER_SELECTED_TAGS = filtersAndPagination.filter.tags;

        this.FILTER_SORT_BY = filtersAndPagination.sort.column;
        this.FILTER_SORT_ASC = this.isSortAsc = filtersAndPagination.sort.asc;
        this.FILTER_LAST_REQUEST_TIME = filtersAndPagination.lastRequestTime;
        this.nameFilterCtrl.setValue(filtersAndPagination.filter.searchName, { emitEvent: false });

        if (!this.selectedListingIdFromUrl) { // Navigate to stored listing only if no id is present in route
          const asset = lodash.find(listingData, { "id": filtersAndPagination.selectedListingId });
          this.selectListing(asset);
        } else {
          this.checkListingFromRoute(); // Just check for the asset as if was from navigation
        }


      } catch (e) {
        console.error("Marketplace stage restore incomplete: ", e);
      }
    }
    this.FILTER_CATEGORIES = this.getFilterCategories(listingData);
    this.filterMarketplaceListingData();
  }

  private filterMarketplaceListingData() {
    let listingData = lodash.cloneDeep(this.listingsData);

    listingData = this.filterBySearchName(listingData);

    listingData = this.filterByAssetType(listingData,
      this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE.getId()]);

    listingData = this.filterByCapability(listingData,
      this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY.getId()]);

    listingData = this.filterListingByMoreFilters(listingData,
      this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER.getId()]);

    listingData = this.filterByMyManagingContributions(listingData,
      this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER.getId()]);

    listingData = this.filterByTags(listingData, this.FILTER_SELECTED_TAGS);

    if (this.tagsList) {
      this.countListingPerTag();
    }

    this.filteredListingsData = listingData;
    // Note: after filter list data, show selected listing data in detail card
    // if the selected listing is not in the filtered listing data, will show empty detail card.
    this.mkService.selectListing(this.filteredListingsData.find(item => item.id === this.selectedListingIdFromUrl));

    this.filterSortBy(this.FILTER_SORT_BY);
    this.storeSelectedFilter();
  }

  private filterListingCountBySubCategory(subCategory: TciMarketplaceV2Filters): number {
    let listingData = lodash.cloneDeep(this.listingsData);

    if (subCategory.getCategory() === TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE) {
      listingData = this.filterByAssetType(listingData, subCategory.id);
    }
    if (subCategory.getCategory() === TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY) {
      listingData = this.filterByCapability(listingData, subCategory.id);
    }
    if (subCategory.getCategory() === TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER) {
      listingData = this.filterCountByMoreFilters(listingData, subCategory.id);
    }
    if (subCategory.getCategory() === TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER) {
      listingData = this.filterByMyManagingContributions(listingData, subCategory.id);
    }
    return listingData.length;
  }

  private filterBySearchName(listingData: ITCIMarketplaceListing[]): ITCIMarketplaceListing[] {
    const name = this.getSearchMKInputText();
    if (name) {
      listingData = listingData
        .filter(item => item.displayName.toLowerCase().trim().indexOf(name.toLowerCase().trim()) >= 0 ||
          item.description.toLowerCase().trim().indexOf(name.toLowerCase().trim()) >= 0);
    }
    return listingData;
  }

  private filterByAssetType(listingData: ITCIMarketplaceListing[], selectedFilter: string): ITCIMarketplaceListing[] {
    if (selectedFilter) {
      listingData = listingData
        .filter(item => selectedFilter === FILTER_LABEL_ALL || item.listingType.toUpperCase() === selectedFilter.toUpperCase());
    }
    return listingData;
  }

  private filterByCapability(listingData: ITCIMarketplaceListing[], selectedFilter: string) {
    if (selectedFilter) {
      listingData = listingData
        .filter(item => {
          return selectedFilter === FILTER_LABEL_ALL ||
            // Note: see client/src/app/ng-marketplace-v2/ng-marketplace-v2.service.ts, getAllListings() -> mapTenantDetails()
            // see the .map() -> r.tenantCategory = xxx, tenantCategory could be null if tenantType is unknown
            (selectedFilter.toUpperCase() === TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_OTHER.label.toUpperCase()
              && item.tenantCategory === null) ||
            (item.tenantCategory && item.tenantCategory.label.toUpperCase() === selectedFilter.toUpperCase());
        });
    }
    return listingData;
  }

  private filterCountByMoreFilters(listingData: ITCIMarketplaceListing[], selectedFilter: string) {
    if (selectedFilter && selectedFilter !== FILTER_LABEL_ALL) {
      listingData = listingData
        .filter(item => {
          return lodash.every(this.getCombinedBooleanByMoreFilters([selectedFilter], item));
        });
    }
    return listingData;
  }

  // show filter number after list page was loaded, the count number will not be changed unless user refresh page
  private filterListingByMoreFilters(listingData: ITCIMarketplaceListing[], selectedFilters: string[] | string) {
    if (selectedFilters && lodash.isArray(selectedFilters)) {
      listingData = listingData
        .filter(item => {
          return lodash.some(this.getCombinedBooleanByMoreFilters(selectedFilters, item));
        });
    }
    return listingData;
  }

  // "My Managing Contributions" filter support for multi selection,
  private getCombinedBooleanByMoreFilters(selectedFilters: string[], item: ITCIMarketplaceListing): boolean[] {
    const bool = [];
    selectedFilters.forEach(filter => {
      if (filter === TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_ORG.id) {
        // I am admin of this listing
        bool.push(item.managingOrgId === this.userInfo.gsbc);
      }
      if (filter === TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_FREE.id) {
        bool.push(item.pricingInfo.pricingOptions.toUpperCase() === filter.toUpperCase());
      }
      if (filter === TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_PAID.id) {
        bool.push(item.pricingInfo.pricingOptions.toUpperCase() === filter.toUpperCase());
      }
      /*
      // disable "Recently Added" feature for now, base on 03/03/2021 meeting discussion.
      if (filter === TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_RECENTLY_ADDED.id && this.FILTER_LAST_REQUEST_TIME) {
        // for private listing, use lastModifiedDate > "my last time request time"
        // for public listing, use lastCertifiedDate > "my last time request time"
        const listingStatus: TCIMarketplaceListingStatus[] = item.listingStatus &&
            item.listingStatus[this.userInfo.gsbc] || [];
        const status = listingStatus.map(s => TCIMarketplaceListingStatus.fromJSON(s));
        if (status.includes(TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE)) {
          bool.push(item.lastModifiedDate > this.FILTER_LAST_REQUEST_TIME);
        }
        if (status.includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE)) {
          bool.push(item.lastCertifiedDate > this.FILTER_LAST_REQUEST_TIME);
        }
      }
      */
    });
    return bool;
  }

  private filterByMyManagingContributions(listingData: ITCIMarketplaceListing[], selectedFilter: string) {
    if (selectedFilter && selectedFilter !== FILTER_LABEL_ALL) {
      listingData = listingData
        .filter(item => {
          let bool = true;
          const listingStatus: TCIMarketplaceListingStatus[] = item.listingStatus &&
            item.listingStatus[this.userInfo.gsbc] || [];
          const status = listingStatus.map(s => TCIMarketplaceListingStatus.fromJSON(s));
          switch (selectedFilter) {
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_APPROVAL.id:
              bool = status.includes(TCIMarketplaceListingStatus.PENDING_APPROVAL);
              break;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PUBLIC_VISIBILITY.id:
              bool = status.includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE);
              break;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PRIVATE_VISIBILITY.id:
              bool = status.includes(TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE);
              break;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_ACCESS_REQUEST.id:
              bool = status.includes(TCIMarketplaceListingStatus.REQUEST_APPROVED);
              break;
          }
          return bool;
        });
    }
    return listingData;
  }

  private filterByTags(listingData: ITCIMarketplaceListing[], selectedTags: ITCIAssetTag[]) {
    if (selectedTags && selectedTags.length > 0) {
      listingData = listingData
        .filter(item => {
          return lodash.intersectionBy(item.tags, selectedTags, "name").length;
        });
    }
    return listingData;
  }

  private countListingPerTag(): void {
    const appCountPerTag = {};
    this.tagsList.forEach(tag => {
      appCountPerTag[tag] = lodash.filter(this.listingsData, item => lodash.find(item.tags, { "name": tag })).length;
    });

    this.allAssetTags = this.tagsService.countTagsWithApps(this.allAssetTags, appCountPerTag);
    this.FILTER_SELECTED_TAGS = this.tagsService.countTagsWithApps(this.FILTER_SELECTED_TAGS, appCountPerTag);

    this.allAssetTagsCount = this.allAssetTags.filter(t => t.assetCount && t.assetCount > 0).length;
    this.selectedTagsCount = this.FILTER_SELECTED_TAGS.filter(t => t.assetCount && t.assetCount > 0).length;
  }

  /**
   * save all the filters(left side filter, sort filter) in local storage
   * IMPORTANT: This method is called at the end of many threads and some of them are linked, making many
   * concurrent calls to it. To avoid performance issues this method is now using a delayed Observable which
   * is reseted on each request as long as it have not finished a previous one.
   */
  private storeSelectedFilter() {
    if (this.storeSelectedFilterWorker) { // If a previous request have not finished then stop it!
      this.storeSelectedFilterWorker.unsubscribe();
    }

    this.storeSelectedFilterWorker = Observable.of(true)
      .delay(350) // Delay the storing to avoid unnecessary events
      .map(() => {
        const filtersAndPagination: IStoredMarketPlaceFilter = {
          filter: {
            assetType: this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE.getId()],
            capability: this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY.getId()],
            more: this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER.getId()],
            myManagingContributions: this.FILTER_SELECTED[TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER.getId()],
            tags: this.FILTER_SELECTED_TAGS,
            searchName: this.getSearchMKInputText()
          },
          sort: {
            column: this.FILTER_SORT_BY,
            asc: this.FILTER_SORT_ASC
          },
          lastRequestTime: this.FILTER_CURRENT_REQUEST_TIME || this.FILTER_LAST_REQUEST_TIME,
          selectedListingId: this.selectedListing ? this.selectedListing.id : ""
        };

        localStorage.setItem(this.getMarketplaceFilterPrefForUser(), JSON.stringify(filtersAndPagination));
        return null;
      })
      .subscribe();
  }

  private getMarketplaceFilterPrefForUser(): string {
    return MK_LOCAL_ITEM_PREFIX + CryptoJS.SHA256(MK_LOCAL_ITEM + this.FILTER_OWNER_NAME).toString();
  }

  private getSearchMKInputText(): string {
    return this.nameFilterCtrl.value ? this.nameFilterCtrl.value.trim() : "";
  }

  /**
   * Initialize watch over the selectedListing$ Subject in order to know when a tile have been selected,
   * either by user interaction or when restoring stage from localStorage.
   */
  private initSelectedListing() {
    this.subscriptions.push(
      this.mkService.getSelectedListingSubject()
        .filter(() => !this.isLoading)
        .subscribe(listing => {
          this.selectedListing = listing;
          this.storeSelectedFilter();

          this.toolsSvc.setTimeout(() => {
            const elementList = document.querySelectorAll(".ng-mk-listing.mode-card.selected");
            if (elementList.length) {
              elementList[0].scrollIntoView({ behavior: "smooth" });
            }
          });
        })
    );
  }
}
